// FILE: src/pages/StudentResourceViewPage.jsx
// The lesson player. Time is the server's (plan WP-3): useLearningSession
// opens a learning session, heartbeats it while the lesson is visible, and
// reports which slide is on screen; the server credits time, enforces the
// daily cap and the break, and decides completion (minimum dwell, D5).
//
// Two ways a lesson reports its slide: a Storyline deck through zun-bridge
// v2 (postMessage), or a SCORM 2004 package through the interim
// `API_1484_11` on this page (WP-4 replaces both with scorm-again).
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useLocation, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CoffeeOutlinedIcon from "@mui/icons-material/CoffeeOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useDispatch, useSelector } from "react-redux";
import { fetchProgress, recordResourceScore } from "../features/progressSlice";
import fetchWithAuth from "../utils/fetchWithAuth";
import { apiUrl } from "../services/api";
import useStorylineBridge from "../hooks/useStorylineBridge";
import useLearningSession from "../hooks/useLearningSession";
import { installInterimScormApi } from "../player/interimScormApi";
import { markActivity } from "../utils/activity";
import { formatDuration } from "../utils/timeFormat";
import { errorBody, errorCode, errorMessage, messageFor } from "../i18n/errors";
import { LoadingState } from "../components/ui";

function CompletionNotice({ completion }) {
  if (!completion) return null;
  if (completion.completed) {
    return (
      <Alert severity="success" sx={{ mt: 1.5 }} data-testid="completion-status">
        Lesson complete. Well done!
      </Alert>
    );
  }
  const slides = completion.slides || [];
  return (
    <Alert severity="info" sx={{ mt: 1.5 }} data-testid="completion-status">
      <AlertTitle>Not finished yet</AlertTitle>
      This lesson isn't complete yet: each slide needs enough viewing time before it counts
      {completion.remainingSec > 0 ? ` (about ${formatDuration(completion.remainingSec)} more)` : ""}.
      {slides.length > 0 && (
        <> Go back to {slides.length === 1 ? "this slide" : "these slides"}: {slides.map((s) => s.name || `Slide ${s.index}`).join(", ")}.</>
      )}
    </Alert>
  );
}

/** Breadcrumbs (Dashboard › lesson), the lesson title as the page's <h1>, and a way back. */
function LessonHeader({ title }) {
  return (
    <Box component="header" sx={{ mb: 2 }}>
      <Breadcrumbs aria-label="Breadcrumb" sx={{ mb: 0.75 }}>
        <Link component={RouterLink} to="/student" color="text.secondary">
          Dashboard
        </Link>
        <Typography color="text.primary" aria-current="page" sx={{ overflowWrap: "anywhere" }}>
          {title}
        </Typography>
      </Breadcrumbs>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 2 }}
        sx={{ alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between" }}
      >
        <Typography variant="h2" component="h1" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          {title}
        </Typography>
        <Link
          component={RouterLink}
          to="/student"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontWeight: 600, flexShrink: 0 }}
        >
          <ArrowBackIcon aria-hidden fontSize="small" />
          Back to dashboard
        </Link>
      </Stack>
    </Box>
  );
}

const NOTICE_TINT = { warning: "zun.warningTint", info: "zun.infoTint", error: "zun.errorTint" };

/** A centred card for a lesson that can't be shown (locked, blocked, failed). */
function LessonNotice({ icon: Icon, tone = "warning", title, children, testId }) {
  return (
    <Box sx={{ display: "grid", placeItems: "center", py: { xs: 3, md: 6 } }}>
      <Card sx={{ width: "100%", maxWidth: 560, textAlign: "center" }} data-testid={testId}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box
            aria-hidden
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 2,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              bgcolor: NOTICE_TINT[tone],
              color: `${tone}.main`
            }}
          >
            <Icon />
          </Box>
          <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography sx={{ mb: 3 }}>{children}</Typography>
          <Button component={RouterLink} to="/student" variant="contained">
            Back to my dashboard
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

const BLOCKED_STYLE = {
  DAILY_LIMIT_REACHED: { icon: HourglassBottomOutlinedIcon, tone: "info", title: "Lessons are closed for today" },
  BREAK_REQUIRED: { icon: CoffeeOutlinedIcon, tone: "warning", title: "Break time" }
};

export default function StudentResourceViewPage() {
  const { search } = useLocation();
  const { resourceId } = useParams();
  const resourceType = new URLSearchParams(search).get("resourceType");
  const dispatch = useDispatch();

  const [loadingResource, setLoadingResource] = useState(true);
  const [resource, setResource] = useState(null);
  const [error, setError] = useState(null); // the failure itself (code + body)
  const iframeRef = useRef(null);
  const everActive = useRef(false);

  const dailyCreditedSec = useSelector((s) => s.time.dailyCreditedSec);
  const untimed = useSelector((s) => s.time.untimed);

  useEffect(() => {
    dispatch(fetchProgress());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    setLoadingResource(true);
    setError(null);
    fetchWithAuth(`/api/resources/${resourceId}`)
      .then((data) => isMounted && setResource(data))
      .catch((err) => isMounted && setError(err))
      .finally(() => isMounted && setLoadingResource(false));
    return () => {
      isMounted = false;
    };
  }, [resourceId]);

  const session = useLearningSession({
    resourceId,
    enabled: Boolean(resource && !resource.locked)
  });
  if (session.phase === "active") everActive.current = true;

  const onScore = (percent) => dispatch(recordResourceScore({ resourceId, percent }));

  // Callbacks change every render; the SCORM API and the bridge read the latest.
  const handlers = useRef({});
  handlers.current = {
    onInitialize: session.setInitialized,
    onLocation: session.setLocation,
    onCompletion: session.requestCompletion,
    onScore,
    onCommit: markActivity
  };

  // Installed before the iframe mounts, so the package finds it on load.
  useLayoutEffect(
    () =>
      installInterimScormApi(window, {
        onInitialize: () => handlers.current.onInitialize(),
        onLocation: (i) => handlers.current.onLocation(i),
        onCompletion: () => handlers.current.onCompletion(),
        onScore: (p) => handlers.current.onScore(p),
        onCommit: () => handlers.current.onCommit()
      }),
    []
  );

  const bridge = useStorylineBridge({
    iframeRef,
    resourceSlides: resource?.slides || [],
    onReady: () => handlers.current.onInitialize(),
    onLocation: (i) => handlers.current.onLocation(i),
    onReachedEnd: () => handlers.current.onCompletion(),
    onScore: (p) => handlers.current.onScore(p)
  });

  // A completed lesson refreshes the progress tables.
  useEffect(() => {
    if (session.completion?.completed) dispatch(fetchProgress());
  }, [session.completion, dispatch]);

  const title = resource?.title || "Lesson";

  if (!resourceType) {
    return (
      <>
        <LessonHeader title="Lesson" />
        <LessonNotice icon={ErrorOutlineIcon} tone="error" title="We couldn't open this lesson">
          This lesson link is incomplete. Open the lesson from your dashboard.
        </LessonNotice>
      </>
    );
  }

  if (loadingResource) {
    return <LoadingState variant="inline" label="Loading lesson…" />;
  }

  // Locked by the server (MTO 13.1): say what to finish first.
  const lockedPrerequisite =
    errorCode(error) === "RESOURCE_LOCKED" ? errorBody(error)?.prerequisite || null : resource?.locked ? resource.prerequisite || null : null;
  if (errorCode(error) === "RESOURCE_LOCKED" || resource?.locked) {
    return (
      <>
        <LessonHeader title={resource ? title : "Lesson"} />
        <LessonNotice icon={LockOutlinedIcon} tone="info" title="This lesson is locked" testId="lesson-blocked">
          {lockedPrerequisite?.title
            ? `Finish ${lockedPrerequisite.title} first. Lessons open in order, one after another.`
            : messageFor("RESOURCE_LOCKED")}
        </LessonNotice>
      </>
    );
  }

  if (error) {
    return (
      <>
        <LessonHeader title="Lesson" />
        <LessonNotice icon={ErrorOutlineIcon} tone="error" title="We couldn't open this lesson">
          {errorMessage(error)}
        </LessonNotice>
      </>
    );
  }

  if (session.phase === "blocked" || session.phase === "error") {
    const style = BLOCKED_STYLE[session.blockedCode] || {
      icon: ErrorOutlineIcon,
      tone: "warning",
      title: "This lesson can't open right now"
    };
    return (
      <>
        <LessonHeader title={title} />
        <LessonNotice icon={style.icon} tone={style.tone} title={style.title} testId="lesson-blocked">
          {messageFor(session.blockedCode || "NETWORK")}
        </LessonNotice>
      </>
    );
  }

  // One URL for everyone: no unlocked "_open" copies (P9, P11).
  const contentUrl = resource?.url ? `/api/content/${resource.url}` : null;
  const showLesson = everActive.current || session.phase === "active";

  return (
    <Box>
      <LessonHeader title={title} />
      {resource?.isPractice && (
        <Alert severity="info" sx={{ mb: 1.5 }} data-testid="practice-notice">
          Practice activity: your answers aren't scored, and it never blocks the next chapter.
        </Alert>
      )}
      <Box
        sx={{
          height: "70vh",
          minHeight: 420,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.4,
          overflow: "hidden",
          bgcolor: "background.paper"
        }}
      >
        {!showLesson ? (
          <Stack spacing={1.5} sx={{ alignItems: "center", justifyContent: "center", height: "100%" }}>
            <CircularProgress aria-label="Opening lesson" />
            <Typography sx={{ color: "text.secondary" }}>Opening your lesson…</Typography>
          </Stack>
        ) : contentUrl ? (
          <iframe
            ref={iframeRef}
            onLoad={bridge.onIframeLoad}
            src={apiUrl(contentUrl)}
            title={`Lesson: ${title}`}
            width="100%"
            height="100%"
            style={{ border: "none", display: "block" }}
          />
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography>This lesson has no content yet. Please contact student support.</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ mt: 1.5 }}>
        {!untimed && (
          <Typography sx={{ color: "text.secondary" }} data-testid="lesson-time">
            Time counted in this lesson: <strong>{formatDuration(session.sessionSec)}</strong>
            {dailyCreditedSec != null && <> · Today: {formatDuration(dailyCreditedSec)} of 5 h</>}
          </Typography>
        )}
        <CompletionNotice completion={session.completion} />
        {bridge.error && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            We couldn't connect to this lesson's progress tracking, so this visit may not be recorded. Please contact
            support.
          </Alert>
        )}
      </Box>
    </Box>
  );
}
